import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Table, { ColumnsType } from 'antd/lib/table';
import { DEFAULT_DATE_FORMAT, getComputerFormattedTime } from 'src/domain/date/date';
import { PjmLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import { PjmLocationMeterInfo } from 'src/domain/pjm/dto/PjmLocationMeterInfo';
import { LocationMeterModal } from './LocationMeterModal';
import { useState } from 'react';
import { PjmMeterEquipmentStatus, PjmMeterType } from 'src/domain/pjm/dto/enums';
import Tooltip from 'antd/lib/tooltip';
import FileOutlined from '@ant-design/icons/FileOutlined';

interface LocationMeterInfoTableProps {
    value: PjmLocationDto['meterInfo'];
    onChange: (value: Array<PjmLocationMeterInfo>) => void;
}

export function LocationMeterInfoTable({ value, onChange }: LocationMeterInfoTableProps) {
    const [showModal, setShowModal] = useState(false);

    function showAddMeterModal() {
        setShowModal(true);
    }

    function onModalClose(actionTaken: boolean) {
        setShowModal(false);
    }

    function onMeterInfoChange(data: PjmLocationMeterInfo) {
        const meters = value || [];
        meters.push(data);
        onChange(meters);
        setShowModal(false);
    }

    const columns: ColumnsType<PjmLocationMeterInfo> = [
        {
            title: 'MeterType',
            dataIndex: 'meterType',
            key: 'meterType',
            fixed: 'left',
        },
        {
            title: 'Meter Equipment',
            dataIndex: 'meterEquipment',
            key: 'meterEquipment',
        },
        {
            title: 'Status',
            dataIndex: 'meterStatus',
            key: 'meterStatus',
        },
        {
            title: 'Review Date',
            dataIndex: 'reviewDate',
            key: 'reviewDate',
            render: (text, record) => <>{text ? getComputerFormattedTime(text, DEFAULT_DATE_FORMAT) : ''}</>,
        },
        {
            title: 'Interval',
            dataIndex: 'interval',
            key: 'interval',
        },
        {
            title: 'Installation Date',
            dataIndex: 'installationDate',
            key: 'installationDate',
            render: (text, record) => <>{text ? getComputerFormattedTime(text, DEFAULT_DATE_FORMAT) : ''}</>,
        },
        {
            title: 'Installation QA Plan',
            dataIndex: 'installationQaPlan',
            key: 'installationQaPlan',
        },
        {
            title: 'Certification',
            dataIndex: 'certification',
            key: 'certification',
        },
    ];

    // Only one Approved or Pending EDC meter is allowed.
    const approvedMeters = (value || []).filter(it => it.meterStatus === PjmMeterEquipmentStatus.APPROVED && it.meterType === PjmMeterType.EDC_METER);
    const pendingMeters = (value || []).filter(it => it.meterStatus === PjmMeterEquipmentStatus.PENDING && it.meterType === PjmMeterType.EDC_METER);

    const canAddMeter = approvedMeters.length === 0 && pendingMeters.length === 0;

    const AddButton = () => {
        if (canAddMeter) {
            return (
                <Button onClick={showAddMeterModal} icon={<FileOutlined />}>
                    Add Meter Equipment
                </Button>
            );
        }

        return (
            <Tooltip title="Only one Approved or Pending EDC meter is allowed!">
                <Button onClick={showAddMeterModal} icon={<FileOutlined />} disabled>
                    Add Meter Equipment
                </Button>
            </Tooltip>
        );
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <AddButton />
            <Table dataSource={value} columns={columns} pagination={false} rowKey="meterType" tableLayout={'auto'} scroll={{ x: 'scroll' }} />
            {showModal && <LocationMeterModal key="pjm-location-meter-modal" onClose={onModalClose} onChange={onMeterInfoChange} />}
        </Space>
    );
}
